import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import bgImage from '../assets/background_login_panel.png';
import { useAuth } from '../context/AuthContext';
import {
  getPasswordStrength,
  validateEmailDomain,
  validatePassword
} from './utils/Register';

const ALLOWED_EMAIL_DOMAIN = 'matplast.pl';
const authContainerStyle = {
  backgroundImage: `url(${bgImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh'
};

const Register = () => {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [verificationPending, setVerificationPending] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const { register, verifyRegistration, resendCode, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Countdown timer for code expiration
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && verificationPending) {
      setLocalError('Verification code has expired. Please request a new one.');
    }
  }, [countdown, verificationPending]);

  // Validate email domain
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    clearError();
    
    // Validate email domain
    if (!validateEmailDomain(email, ALLOWED_EMAIL_DOMAIN)) {
      setLocalError(`Registration is only allowed for @${ALLOWED_EMAIL_DOMAIN} email addresses`);
      return;
    }
    
    // Validate password
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setLocalError(`Password must contain: ${passwordErrors.join(', ')}`);
      return;
    }
    
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    const result = await register(email, fullName, password);
    
    setIsLoading(false);
    
    if (result && result.requires_verification) {
      setVerificationPending(result);
      setCountdown(result.expires_in_seconds);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLocalError('');
    clearError();
    
    if (countdown <= 0) {
      setLocalError('Verification code has expired. Please request a new one.');
      setIsLoading(false);
      return;
    }
    
    const success = await verifyRegistration(verificationPending.user_id, verificationCode);
    
    setIsLoading(false);
    if (success) {
      navigate('/login');
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setLocalError('');
    clearError();
    
    const result = await resendCode(verificationPending.user_id);
    
    setIsLoading(false);
    if (result && result.expires_in_seconds) {
      setCountdown(result.expires_in_seconds);
      setVerificationCode('');
      setLocalError('');
    }
  };

  const passwordStrength = getPasswordStrength();

  // Verification code entry screen
  if (verificationPending) {
    return (
      <div
        className="auth-container notranslate"
        translate="no"
        style={authContainerStyle}
      >
        <div className="auth-card">
          <h2>Verify Your Email</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '1rem' }}>
            A verification code has been sent to<br />
            <strong>{verificationPending.email}</strong>
          </p>
          
          {(error || localError) && (
            <div className="error-message">
              {error || localError}
            </div>
          )}
          
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '1rem',
            padding: '10px',
            backgroundColor: countdown > 30 ? '#dcfce7' : countdown > 0 ? '#fef3c7' : '#fee2e2',
            borderRadius: '8px',
            fontWeight: 'bold'
          }}>
            {countdown > 0 ? (
              <>Time remaining: <span style={{ color: countdown > 30 ? '#16a34a' : '#d97706' }}>{countdown}s</span></>
            ) : (
              <span style={{ color: '#dc2626' }}>Code expired</span>
            )}
          </div>
          
          <form onSubmit={handleVerifySubmit}>
            <div className="form-group">
              <label htmlFor="code">Verification Code</label>
              <input
                type="text"
                id="code"
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                  setLocalError('');
                  clearError();
                }}
                placeholder="Enter the code from your email"
                required
                autoComplete="off"
                style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '16px',
                  letterSpacing: '2px',
                  textAlign: 'center'
                }}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading || countdown <= 0}
            >
              {isLoading ? 'Verifying...' : 'Verify & Activate Account'}
            </button>
          </form>
          
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button 
              onClick={handleResendCode}
              disabled={isLoading}
              className="btn btn-secondary"
            >
              Resend Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div
      className="auth-container notranslate"
      translate="no"
      style={authContainerStyle}
    >
      <div className="auth-card">
        <h2>Utwórz konto</h2>
        
        <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '1rem' }}>
          Rejestracja jest ograniczona do adresów email z domeną <strong>@{ALLOWED_EMAIL_DOMAIN}</strong>. <br />
        </p>
        
        {(error || localError) && (
          <div className="error-message">
            {error || localError}
          </div>
        )}
        
        <form onSubmit={handleRegisterSubmit}>
          <div className="form-group">
            <label htmlFor="fullName">Imię i nazwisko</label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                setLocalError('');
                clearError();
              }}
              placeholder="Wpisz swoje imię i nazwisko"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setLocalError('');
                clearError();
              }}
              placeholder={`Wpisz swój email @${ALLOWED_EMAIL_DOMAIN}`}
              required
              style={{
                borderColor: email && !validateEmailDomain(email, ALLOWED_EMAIL_DOMAIN) ? '#dc2626' : undefined
              }}
            />
            {email && !validateEmailDomain(email, ALLOWED_EMAIL_DOMAIN) && (
              <small style={{ color: '#dc2626' }}>
                Tylko adresy email @{ALLOWED_EMAIL_DOMAIN} są dozwolone
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Hasło</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setLocalError('');
              }}
              placeholder="Wpisz swoje hasło"
              required
              minLength={8}
            />
            {passwordStrength && (
              <small style={{ color: passwordStrength.color, display: 'block', marginTop: '4px' }}>
                {passwordStrength.text}
              </small>
            )}
            <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
              Wymagania: 8+ znaków, 1 wielka litera, 1 znak specjalny
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Potwierdź hasło</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setLocalError('');
              }}
              placeholder="Potwierdź swoje hasło"
              required
              style={{
                borderColor: confirmPassword && password !== confirmPassword ? '#dc2626' : undefined
              }}
            />
            {confirmPassword && password !== confirmPassword && (
              <small style={{ color: '#dc2626' }}>
                Hasła nie pasują
              </small>
            )}
          </div>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Tworzenie konta...' : 'Zarejestruj się'}
          </button>
        </form>
        
        <div className="auth-switch">
          Masz już konto? <Link to="/login">Zaloguj się</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
